/**
 * Countries where an affirmative opt-in is required before any non-essential
 * cookies / device storage / advertising tags fire.
 *
 * Default set (30): the European Economic Area (EU + IS, LI, NO), the United
 * Kingdom (UK GDPR + PECR), and Switzerland (revFADP parallel posture).
 *
 * Decision points before production:
 *  - Quebec Law 25 is comparable to GDPR but country-level geo cannot
 *    disambiguate Quebec from the rest of Canada. Today CA is NOT in this
 *    set; Quebec is addressed via the Phase 6 French-notice work instead.
 *  - Some jurisdictions (Brazil LGPD, South Korea PIPA, Singapore PDPA) are
 *    GDPR-comparable but operate more like opt-out-with-notice in practice;
 *    we cover them via Privacy Notice + DSR endpoints, not the banner.
 *  - Trimming this list (e.g., to a subset the privacy team confirms is
 *    in-scope) requires legal sign-off. The constant below is the single
 *    edit point — do not duplicate the list anywhere else.
 *
 * Sources:
 *  - EU members: https://european-union.europa.eu/principles-countries-history/eu-countries_en
 *  - EEA non-EU: Iceland, Liechtenstein, Norway (EEA Agreement)
 *  - UK GDPR + Privacy and Electronic Communications Regulations
 *  - Swiss Federal Act on Data Protection (revFADP, effective 2023-09-01)
 */
export const GDPR_COUNTRIES: ReadonlySet<string> = new Set([
  // European Union (27)
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA non-EU
  'IS', 'LI', 'NO',
  // United Kingdom (UK GDPR)
  'GB',
  // Switzerland (revFADP — parallel posture)
  'CH',
]);

/**
 * Returns true when an EEA/UK/Swiss opt-in surface is required for the given
 * country code. Pass the value from `useViewerCountry().country` directly.
 * `null` (unknown / dev / missing CF header) returns false — fail-closed:
 * no banner is shown, and no non-essential tracking loads either.
 */
export function isGdprCountry(country: string | null | undefined): boolean {
  return !!country && GDPR_COUNTRIES.has(country);
}
